import styled, { css } from 'styled-components'

export const HeaderRoot = styled.header`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 1.25rem;
  padding: 3.5rem 2rem 0;
  margin-bottom: 1.5rem;
`

export const HeaderAvatarBlock = styled.div`
  position: relative;
  width: 5rem;
  height: 5rem;
  flex-shrink: 0;
`

export const HeaderAvatar = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  background: ${({ theme }) => theme.grey200Color};
`

export const HeaderPlatformBadge = styled.span`
  position: absolute;
  right: -0.125rem;
  bottom: -0.125rem;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  background: ${({ theme }) => theme.whiteColor};
  border: 0.125rem solid ${({ theme }) => theme.whiteColor};
  box-shadow: 0 0.125rem 0.25rem rgb(0 0 0 / 12%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`

export const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
  min-width: 0;
`

export const HeaderPillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
`

export const HeaderName = styled.h2`
  margin: 0;
  font-size: 1.875rem;
  font-weight: 400;
  line-height: 1.2;
  letter-spacing: 0.0187rem;
  color: #4d4d4d;
  font-family: Rubik, verdana, sans-serif;
`

export const HeaderMeta = styled.div`
  font-size: 1rem;
  color: #808080;
  font-family: Rubik, verdana, sans-serif;
`

// BRAND-9635: sits between the meta line and the pills. The address is rendered in
// full and stays selectable — a CM copies it or reads it off to run outreach, so
// truncating it into an ellipsis would defeat the point of surfacing it at all.
export const HeaderEmailRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.375rem;
  font-size: 0.875rem;
  font-family: Rubik, verdana, sans-serif;
`

export const HeaderEmailValue = styled.span`
  color: ${({ theme }) => theme.grey900Color};
  user-select: all;
  overflow-wrap: anywhere;
`

export const HeaderCopyButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.primaryColor};

  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.primaryColor};
    outline-offset: 0.125rem;
  }
`

export const HeaderPlatformPill = styled.span<{ $onPlatform: boolean }>`
  display: inline-flex;
  padding: 0.125rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  ${({ $onPlatform, theme }) =>
    $onPlatform
      ? css`
          background: ${theme.secondary100Color};
          color: ${theme.secondaryColor};
        `
      : css`
          background: ${theme.primary100Color};
          color: ${theme.primaryColor};
        `}
`

// Mirrors HeaderPlatformPill's shape so it reads as a sibling badge (BRAND-9488);
// reuses the same green already used for the approve action button below.
export const HeaderRosterPill = styled.span`
  display: inline-flex;
  padding: 0.125rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  background: #f00;
  color: #fff;
`

export const ActionStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: stretch;
`

const HEADER_VARIANT_BG: Record<'approve' | 'reject' | 'undo', string> = {
  approve: 'rgba(237, 252, 238, 0.5)',
  reject: 'rgba(255, 241, 241, 0.5)',
  undo: 'rgba(229, 229, 229, 0.5)',
}

const HEADER_VARIANT_FG: Record<'approve' | 'reject' | 'undo', string> = {
  approve: '#01D36D',
  reject: '#FC5151',
  undo: '#808080',
}

const HEADER_VARIANT_HOVER_BG: Record<'approve' | 'reject' | 'undo', string> = {
  approve: '#EDFCEE',
  reject: '#FFF1F1',
  undo: '#E5E5E5',
}

export const HeaderActionButton = styled.button<{ variant: 'approve' | 'reject' | 'undo' }>`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.625rem;
  min-width: 8.25rem;
  height: 2.5rem;
  padding: 0 1rem;
  border: none;
  border-radius: 1.25rem;
  background: ${({ variant }) => HEADER_VARIANT_BG[variant]};
  color: ${({ variant }) => HEADER_VARIANT_FG[variant]};
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  cursor: pointer;
  transition: background 0.15s ease;

  svg {
    flex-shrink: 0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.primaryColor};
    outline-offset: 0.125rem;
  }

  &:hover:not(:disabled),
  &:active:not(:disabled) {
    background: ${({ variant }) => HEADER_VARIANT_HOVER_BG[variant]};
  }
`

// Heart/shortlist link in the drawer header. Mirrors the row's heart action:
// transparent by default, primary100 background tint on hover, red foreground.
export const HeaderShortlistButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 0.75rem;
  border: none;
  border-radius: 1.25rem;
  background: transparent;
  color: #fc5151;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  cursor: pointer;
  transition: background 0.15s ease;

  svg {
    flex-shrink: 0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.primaryColor};
    outline-offset: 0.125rem;
  }

  &:hover:not(:disabled),
  &:active:not(:disabled) {
    background: ${({ theme }) => theme.primary100Color};
  }
`
